import { useState, useMemo, useCallback, useRef } from 'react';
import useSmartyAddressSearchProvider from './useSmartyAddressSearchProvider';

const MIN_SEARCH_LENGTH = 4;
export const STATUS_IDLE = 'idle';
export const STATUS_TYPING = 'typing';
export const STATUS_LOADING = 'loading';
export const STATUS_NO_RESULTS = 'no_results';
export const STATUS_HAS_RESULTS = 'has_results';

const useSmartyAddressAutocompleteController = ({ dispatch, formFieldId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [lastFetchedValue, setLastFetchedValue] = useState('');
  const [hasFetched, setHasFetched] = useState(false);
  const pendingRequestRef = useRef(0);
  const requestIdRef = useRef(0);

  const {
    fetchPredictions,
    debouncedFetchPredictions,
    cancelDebounced,
    buildSecondarySearchQuery,
  } = useSmartyAddressSearchProvider({ dispatch, formFieldId });

  const fetchAddressPredictions = useCallback(
    (searchText, selected = null) => {
      if (!searchText || searchText.trim().length < MIN_SEARCH_LENGTH) {
        setSuggestions([]);
        setIsLoading(false);
        return Promise.resolve([]);
      }

      setIsLoading(true);
      setHasFetched(false);
      setLastFetchedValue(searchText);
      pendingRequestRef.current += 1;
      const requestId = pendingRequestRef.current;

      return fetchPredictions(searchText, selected)
        .then((next) => {
          if (requestId !== pendingRequestRef.current) {
            return [];
          }
          setSuggestions(next);
          return next;
        })
        .finally(() => {
          if (requestId === pendingRequestRef.current) {
            setIsLoading(false);
            setHasFetched(true);
          }
        });
    },
    [fetchPredictions],
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setIsLoading(false);
  }, []);

  const resetFetchState = useCallback(() => {
    setHasFetched(false);
    setLastFetchedValue('');
  }, []);

  const resetInputStateIfShort = useCallback(
    (normalizedValue) => {
      if (normalizedValue.length < MIN_SEARCH_LENGTH) {
        cancelDebounced();
        requestIdRef.current += 1;
        clearSuggestions();
      }

      if (normalizedValue.length < 2) {
        resetFetchState();
      }
    },
    [cancelDebounced, clearSuggestions, resetFetchState],
  );

  const startFetchStateIfReady = useCallback((normalizedValue, nextValue) => {
    if (normalizedValue.length >= MIN_SEARCH_LENGTH) {
      setIsLoading(true);
      setHasFetched(false);
      setLastFetchedValue(nextValue || '');
    }
  }, []);

  const requestPredictionsIfReady = useCallback(
    (normalizedValue, nextValue) => {
      if (normalizedValue.length < MIN_SEARCH_LENGTH) {
        cancelDebounced();
        requestIdRef.current += 1;
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      const rawValue = nextValue || '';
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      debouncedFetchPredictions(rawValue, null, requestId, (results, returnedRequestId) => {
        if (returnedRequestId !== requestIdRef.current) {
          return;
        }

        if (!rawValue || rawValue.trim().length < MIN_SEARCH_LENGTH) {
          setSuggestions([]);
          setIsLoading(false);
          return;
        }

        setSuggestions(results);
        setIsLoading(false);
        setHasFetched(true);
      });
    },
    [cancelDebounced, debouncedFetchPredictions],
  );

  const handleInputValueChange = useCallback(
    (nextValue, { onChange, onSelectAddress, isRefiningSearch }) => {
      const normalizedValue = (nextValue || '').trim();

      if (!isRefiningSearch) {
        setSelectedAddress(null);
        onChange(nextValue || '');
      }

      setInputValue(nextValue || '');

      resetInputStateIfShort(normalizedValue);
      startFetchStateIfReady(normalizedValue, nextValue);
      requestPredictionsIfReady(normalizedValue, nextValue);

      if (onSelectAddress) {
        onSelectAddress(null);
      }
    },
    [resetInputStateIfShort, startFetchStateIfReady, requestPredictionsIfReady],
  );

  const handleAddressRefinement = useCallback(
    (suggestion, { onChange, setIsRefiningSearch }) => {
      cancelDebounced();
      setIsRefiningSearch(true);

      const { search, selected } = buildSecondarySearchQuery(suggestion);
      onChange(search);
      fetchAddressPredictions(search, selected).finally(() => {
        setIsRefiningSearch(false);
      });
    },
    [cancelDebounced, buildSecondarySearchQuery, fetchAddressPredictions],
  );

  const handleSingleAddressSelection = useCallback(
    (suggestion, { onChange, onSelectAddress, onBlur }) => {
      onChange(suggestion.description);

      if (onSelectAddress) {
        onSelectAddress(suggestion.description);
        setSelectedAddress(suggestion.description);
      }

      if (onBlur) {
        onBlur(suggestion.description);
      }
    },
    [],
  );

  const status = useMemo(() => {
    const trimmed = inputValue.trim();
    const length = trimmed.length;

    if (suggestions.length > 0) return STATUS_HAS_RESULTS;
    if (length >= 4 && isLoading) return STATUS_LOADING;
    if (length >= 4 && !isLoading && hasFetched) return STATUS_NO_RESULTS;
    if (length >= 2 && length <= 3) return STATUS_TYPING;
    return STATUS_IDLE;
  }, [inputValue, isLoading, hasFetched, suggestions.length]);

  return {
    suggestions,
    selectedAddress,
    status,
    lastFetchedValue,
    fetchAddressPredictions,
    debouncedFetchPredictions,
    cancelDebounced,
    clearSuggestions,
    handleInputValueChange,
    handleAddressRefinement,
    handleSingleAddressSelection,
  };
};

export default useSmartyAddressAutocompleteController;
